/**
 * Represents a time interval.
 */
export default class TimeSpan {
	/**
	 * The total number of milliseconds that the TimeSpan represents
	 */
	totalMilliseconds: number;

	/**
	 * Creates a TimeSpan instance that represents a time interval.
	 * @param totalMilliseconds The total number of milliseconds that the TimeSpan represents
	 */
	constructor(totalMilliseconds: number) {
		this.totalMilliseconds = totalMilliseconds;
	}

	/**
	 * Gets the value of the TimeSpan expressed in whole and fractional milliseconds.
	 */
	get totalSeconds(): number {
		return this.totalMilliseconds / 1000;
	}

	/**
	 * Gets the value of the TimeSpan expressed in whole and fractional seconds.
	 */
	get totalMinutes(): number {
		return this.totalSeconds / 60;
	}

	/**
	 * Gets the value of the TimeSpan expressed in whole and fractional minutes.
	 */
	get totalHours(): number {
		return this.totalMinutes / 60;
	}

	/**
	 * Gets the time interval in seconds
	 */
	get totalDays(): number {
		return this.totalHours / 24;
	}

	/**
	 * Gets the milliseconds component of the time interval represented by the TimeSpan.
	 */
	get milliseconds(): number {
		return Math.floor(this.totalMilliseconds % 1000);
	}

	/**
	 * Gets the seconds component of the time interval represented by the TimeSpan.
	 */
	get seconds(): number {
		return Math.floor(this.totalSeconds % 60);
	}

	/**
	 * Gets the minutes component of the time interval represented by the TimeSpan.
	 */
	get minutes(): number {
		return Math.floor(this.totalMinutes % 60);
	}

	/**
	 * Gets the hours component of the time interval represented by the TimeSpan.
	 */
	get hours(): number {
		return Math.floor(this.totalHours % 24);
	}

	/**
	 * Gets the days component of the time interval represented by the TimeSpan.
	 */
	get days(): number {
		return Math.floor(this.totalDays);
	}

	/**
	 * Converts the timespan to it's millisecond value
	 */
	valueOf() {
		return this.totalMilliseconds;
	}

	/**
	 * Converts the TimeSpan to a friendly string representation
	 */
	toString() {
		let num: number;
		let label: string;

		if (this.totalHours < 1) {
			num = Math.round(this.totalMinutes);
			label = 'minute';
		}
		else if (this.totalDays < 1) {
			num = Math.round(this.totalHours * 100) / 100;
			label = 'hour';
		}
		else {
			num = Math.round(this.totalDays * 100) / 100;
			label = 'day';
		}

		return num === 1 ? num + ' ' + label : num + ' ' + label + 's';
	}
}
